import * as THREE from 'three';
import {
  applySurfaceMaterialDefaults,
  copySurfaceMaterialProperties,
  surfaceProgramCacheKey,
  type SurfaceMaterialParams,
} from '../core/SurfaceMaterial';
import { applyBakeShaderHooks } from '../core/SurfaceBakeChunks';
import {
  assignDamageFieldUniforms,
  DAMAGE_FIELD_COMMON_GLSL,
  DAMAGE_FIELD_GLSL,
  DAMAGE_FIELD_VERTEX_GLSL,
  DAMAGE_FIELD_VERTEX_SAMPLE,
  damageFieldProgramParts,
  registerDamageFieldConsumer,
} from '../core/DamageFieldBinding';
import { SURFACE_MICRO_GLSL } from '../core/SurfaceMicroDetail';
import { SURFACE_NOISE_GLSL } from '../core/SurfaceNoise';
import { SURFACE_TRIPLANAR_GLSL } from '../core/TriplanarProjection';
import type { MaterialAppearanceDescriptor } from '../appearanceDescriptor';
import {
  asFiniteNumber,
  asHexColor,
  commonSurfaceControls,
  type SurfaceControlSpec,
  type SurfaceDefinition,
  type SurfaceFace,
} from '../core/SurfaceDefinition';
import { DESTRUCTION_PROFILES } from '../../services/destructionProfiles';

export type PlasterQuality = 'high' | 'medium' | 'low';
export type PlasterFace = SurfaceFace;

export interface PlasterMaterialSettings {
  textureScale: number;
  roughness: number;
  normalStrength: number;
  seed: number;
  quality: PlasterQuality;
  baseTint: string;
  roughnessMin: number;
  roughnessMax: number;
  wearAmount: number;
  /** First-octave wavelength inverse for the broad fBm (default 0.38). */
  broadScale: number;
  /** First-octave frequency for the pore fBm (default 3.4). */
  poreScale: number;
  clearcoat: number;
  clearcoatRoughness: number;
}

export const DEFAULT_PLASTER_SETTINGS: PlasterMaterialSettings = {
  textureScale: 1,
  roughness: 0.76,
  normalStrength: 0.14,
  seed: 4091,
  quality: 'medium',
  baseTint: '#d8d0c4',
  roughnessMin: 0.55,
  roughnessMax: 0.98,
  wearAmount: 0.2,
  broadScale: 0.38,
  poreScale: 3.4,
  clearcoat: 0.08,
  clearcoatRoughness: 0.76,
};

/** Pale chalky dust, off-white chips — matches the plaster albedo. */
export const appearanceDescriptor = {
  version: 1,
  dust: { color: '#e8e0d4', lingerScale: 1.15, settleSpeed: 0.22 },
  chips: { color: '#f0eadc' },
  debrisTint: '#ddd4c4',
  particleStyle: { plume: true },
  fragment: { outer: '#d4cdc2', interior: '#efe8dc' },
} as const satisfies MaterialAppearanceDescriptor;

interface PlasterUniforms {
  uPlasterTextureScale: { value: number };
  uPlasterNormalStrength: { value: number };
  uPlasterSeed: { value: number };
  uPlasterQuality: { value: number };
  uPlasterInteriorMode: { value: number };
  uPlasterBaseTint: { value: THREE.Color };
  uPlasterRoughnessMin: { value: number };
  uPlasterRoughnessMax: { value: number };
  uPlasterWearAmount: { value: number };
  uPlasterBroadScale: { value: number };
  uPlasterPoreScale: { value: number };
  uPlasterClearcoat: { value: number };
  uPlasterClearcoatRoughness: { value: number };
}

interface PlasterMaterialState {
  face: PlasterFace;
  settings: PlasterMaterialSettings;
  uniforms: PlasterUniforms;
}

const plasterStates = new WeakMap<THREE.Material, PlasterMaterialState>();

const PLASTER_GLSL = /* glsl */ `
uniform float uPlasterTextureScale;
uniform float uPlasterNormalStrength;
uniform float uPlasterSeed;
uniform float uPlasterQuality;
uniform float uPlasterInteriorMode;
uniform vec3 uPlasterBaseTint;
uniform float uPlasterRoughnessMin;
uniform float uPlasterRoughnessMax;
uniform float uPlasterWearAmount;
uniform float uPlasterBroadScale;
uniform float uPlasterPoreScale;
uniform float uPlasterClearcoat;
uniform float uPlasterClearcoatRoughness;

float plasterFbm(vec3 p, float wavelengthMeters, float fp) {
  float value = 0.0;
  float amp = 1.0;
  float wsum = 0.0;
  float wl = max(wavelengthMeters, 1e-4);
  vec3 q = p;
  // 4 octaves, lacunarity 2.07, persistence 0.51. Each octave band-limited.
  value += amp * surfaceNoiseBL(q, uPlasterSeed * 11.17, wl, fp);
  wsum += amp;
  q = q * 2.07 + vec3(17.0, 43.0, 29.0);
  wl /= 2.07;
  amp *= 0.51;
  value += amp * surfaceNoiseBL(q, uPlasterSeed * 11.17, wl, fp);
  wsum += amp;
  q = q * 2.07 + vec3(31.0, 11.0, 47.0);
  wl /= 2.07;
  amp *= 0.51;
  value += amp * surfaceNoiseBL(q, uPlasterSeed * 11.17, wl, fp);
  wsum += amp;
  q = q * 2.07 + vec3(7.0, 53.0, 19.0);
  wl /= 2.07;
  amp *= 0.51;
  value += amp * surfaceNoiseBL(q, uPlasterSeed * 11.17, wl, fp);
  wsum += amp;
  return value / max(wsum, 1e-5);
}

void plasterSurfaceFields(vec3 restPos, out float broad, out float pores, out float height) {
  float scale = max(uPlasterTextureScale, 0.05);
  float fp = surfacePixelFootprint(restPos);
  vec3 p = restPos / scale;
  // broadScale is first-octave WAVELENGTH in metres (default 0.38). Feeding 0.38
  // as a frequency produced ~2.6 m cells — a close-up held <1 lobe and read as
  // a lighting gradient, which is why Phase A plaster looked like smooth plastic.
  float broadWl = max(uPlasterBroadScale, 0.05) * scale;
  float poreFreq = max(uPlasterPoreScale, 0.2);
  float poreWl = scale / poreFreq;
  broad = plasterFbm(p * (scale / broadWl) + vec3(5.0, 13.0, 7.0), broadWl, fp);
  float poresBroad = plasterFbm(p * poreFreq + vec3(19.0, 3.0, 31.0), poreWl, fp);
  // Finer pore octave stack (~poreWl/4) so close-ups show speckle, still band-limited.
  float poreFineWl = poreWl / 4.14;
  float poresFine = plasterFbm(p * poreFreq * 4.14 + vec3(8.0, 21.0, 5.0), poreFineWl, fp);
  float poreFade = surfaceOctaveFade(poreFineWl, fp);
  pores = mix(poresBroad, poresFine, 0.48 * poreFade);
  if (uPlasterInteriorMode > 0.5) {
    // Fresh break: drop the weathered pore pattern; keep a coarser fracture grain.
    float fracWl = max(broadWl * 0.55, 0.08);
    broad = plasterFbm(p * (scale / fracWl) + vec3(11.0, 4.0, 29.0), fracWl, fp);
    pores = plasterFbm(p * (scale / (fracWl * 0.45)) + vec3(3.0, 17.0, 9.0), fracWl * 0.45, fp);
    height = 0.5 + (broad - 0.5) * 0.22 + (pores - 0.5) * 0.16;
  } else {
    height = 0.5 + (broad - 0.5) * 0.07 + (pores - 0.5) * 0.08 * poreFade;
  }
}
`;

const PLASTER_VERTEX_INJECTION = /* glsl */ `
attribute vec3 surfaceRestPosition;
varying vec3 vPlasterRestPosition;
varying vec3 vPlasterRestNormal;
${DAMAGE_FIELD_COMMON_GLSL}
${DAMAGE_FIELD_VERTEX_GLSL}
`;

const PLASTER_FRAGMENT_INJECTION = /* glsl */ `
uniform mat3 normalMatrix;
varying vec3 vPlasterRestPosition;
varying vec3 vPlasterRestNormal;
${SURFACE_TRIPLANAR_GLSL}
${SURFACE_NOISE_GLSL}
${SURFACE_MICRO_GLSL}
${DAMAGE_FIELD_GLSL}
${PLASTER_GLSL}
float gPlasterBroad;
float gPlasterPores;
float gPlasterHeight;
`;

function clamp01(value: number, fallback = 0): number {
  return Math.min(Math.max(Number.isFinite(value) ? value : fallback, 0), 1);
}

function qualityValue(quality: PlasterQuality): number {
  return quality === 'high' ? 2 : quality === 'medium' ? 1 : 0;
}

function normalizeSettings(settings: PlasterMaterialSettings): PlasterMaterialSettings {
  const roughnessMin = clamp01(asFiniteNumber(settings.roughnessMin, DEFAULT_PLASTER_SETTINGS.roughnessMin));
  const roughnessMax = clamp01(asFiniteNumber(settings.roughnessMax, DEFAULT_PLASTER_SETTINGS.roughnessMax));
  return {
    textureScale:
      Number.isFinite(settings.textureScale) && settings.textureScale > 0
        ? settings.textureScale
        : DEFAULT_PLASTER_SETTINGS.textureScale,
    roughness: clamp01(settings.roughness, DEFAULT_PLASTER_SETTINGS.roughness),
    normalStrength: Math.min(Math.max(Number.isFinite(settings.normalStrength) ? settings.normalStrength : 0, 0), 0.8),
    seed: Number.isFinite(settings.seed) ? settings.seed : DEFAULT_PLASTER_SETTINGS.seed,
    quality: settings.quality === 'high' || settings.quality === 'low' ? settings.quality : 'medium',
    baseTint: asHexColor(settings.baseTint, DEFAULT_PLASTER_SETTINGS.baseTint),
    roughnessMin: Math.min(roughnessMin, roughnessMax),
    roughnessMax: Math.max(roughnessMin, roughnessMax),
    wearAmount: clamp01(asFiniteNumber(settings.wearAmount, DEFAULT_PLASTER_SETTINGS.wearAmount)),
    broadScale:
      Number.isFinite(settings.broadScale) && settings.broadScale > 0
        ? settings.broadScale
        : DEFAULT_PLASTER_SETTINGS.broadScale,
    poreScale:
      Number.isFinite(settings.poreScale) && settings.poreScale > 0
        ? settings.poreScale
        : DEFAULT_PLASTER_SETTINGS.poreScale,
    clearcoat: clamp01(asFiniteNumber(settings.clearcoat, DEFAULT_PLASTER_SETTINGS.clearcoat)),
    clearcoatRoughness: clamp01(
      asFiniteNumber(settings.clearcoatRoughness, DEFAULT_PLASTER_SETTINGS.clearcoatRoughness),
    ),
  };
}

function createUniforms(settings: PlasterMaterialSettings, face: PlasterFace): PlasterUniforms {
  return {
    uPlasterTextureScale: { value: settings.textureScale },
    uPlasterNormalStrength: { value: settings.normalStrength },
    uPlasterSeed: { value: settings.seed },
    uPlasterQuality: { value: qualityValue(settings.quality) },
    uPlasterInteriorMode: { value: face === 'interior' ? 1 : 0 },
    uPlasterBaseTint: { value: new THREE.Color(settings.baseTint) },
    uPlasterRoughnessMin: { value: settings.roughnessMin },
    uPlasterRoughnessMax: { value: settings.roughnessMax },
    uPlasterWearAmount: { value: settings.wearAmount },
    uPlasterBroadScale: { value: settings.broadScale },
    uPlasterPoreScale: { value: settings.poreScale },
    uPlasterClearcoat: { value: settings.clearcoat },
    uPlasterClearcoatRoughness: { value: settings.clearcoatRoughness },
  };
}

function syncUniforms(state: PlasterMaterialState): void {
  const { settings, uniforms, face } = state;
  uniforms.uPlasterTextureScale.value = settings.textureScale;
  uniforms.uPlasterNormalStrength.value = settings.normalStrength;
  uniforms.uPlasterSeed.value = settings.seed;
  uniforms.uPlasterQuality.value = qualityValue(settings.quality);
  uniforms.uPlasterInteriorMode.value = face === 'interior' ? 1 : 0;
  uniforms.uPlasterBaseTint.value.set(settings.baseTint);
  uniforms.uPlasterRoughnessMin.value = settings.roughnessMin;
  uniforms.uPlasterRoughnessMax.value = settings.roughnessMax;
  uniforms.uPlasterWearAmount.value = settings.wearAmount;
  uniforms.uPlasterBroadScale.value = settings.broadScale;
  uniforms.uPlasterPoreScale.value = settings.poreScale;
  uniforms.uPlasterClearcoat.value = settings.clearcoat;
  uniforms.uPlasterClearcoatRoughness.value = settings.clearcoatRoughness;
}

function compilePlasterShader(
  shader: { uniforms: Record<string, { value: unknown }>; vertexShader: string; fragmentShader: string },
  state: PlasterMaterialState,
): void {
  Object.assign(shader.uniforms, state.uniforms);
  assignDamageFieldUniforms(shader);
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', `${PLASTER_VERTEX_INJECTION}\n#include <common>`)
    .replace(
      '#include <worldpos_vertex>',
      `#include <worldpos_vertex>
vPlasterRestPosition = surfaceRestPosition;
vPlasterRestNormal = normalize(normal);
${DAMAGE_FIELD_VERTEX_SAMPLE}
`,
    );

  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', `${PLASTER_FRAGMENT_INJECTION}\n#include <common>`)
    .replace(
      '#include <color_fragment>',
      `#include <color_fragment>
gSurfaceSpecAaRough = 0.0;
plasterSurfaceFields(vPlasterRestPosition, gPlasterBroad, gPlasterPores, gPlasterHeight);
float plasterMicro = surfaceMicroField(vPlasterRestPosition, max(uPlasterTextureScale * 0.09, 0.012), uPlasterSeed + 29.0);
float plasterLodFade = surfaceMicroFade(vPlasterRestPosition, max(uPlasterTextureScale * 0.09, 0.012));
vec3 plasterAlbedo = uPlasterBaseTint * mix(0.91, 1.04, gPlasterBroad);
// Pores must live in albedo, not only roughness — roughness-only pores vanish
// under the lab's frontal key and read as smooth plastic. Dark pinholes (like
// concrete's poreTint mix) are what actually survive AgX on a bright wall.
plasterAlbedo *= mix(0.94, 1.03, gPlasterPores);
float plasterPitFade = surfaceOctaveFade(max(uPlasterTextureScale, 0.05) / max(uPlasterPoreScale, 0.2) / 4.14, surfacePixelFootprint(vPlasterRestPosition));
float plasterPitHalf = mix(0.05, 0.12, 1.0 - plasterPitFade);
float plasterPits = smoothstep(0.72 - plasterPitHalf, 0.72 + plasterPitHalf, gPlasterPores) * plasterPitFade;
plasterAlbedo *= mix(1.0, 0.62, plasterPits);
float plasterSpeckWl = max(uPlasterTextureScale, 0.05) / max(uPlasterPoreScale, 0.2) / 8.5;
float plasterSpeckFade = surfaceOctaveFade(plasterSpeckWl, surfacePixelFootprint(vPlasterRestPosition));
float plasterSpeckHalf = mix(0.04, 0.10, 1.0 - plasterSpeckFade);
float plasterSpeck = surfaceNoiseBL(
  vPlasterRestPosition / max(uPlasterTextureScale, 0.05) * max(uPlasterPoreScale, 0.2) * 8.5 + vec3(14.0, 6.0, 22.0),
  uPlasterSeed * 11.17,
  plasterSpeckWl,
  surfacePixelFootprint(vPlasterRestPosition)
);
float plasterPin = smoothstep(0.84 - plasterSpeckHalf, 0.84 + plasterSpeckHalf, plasterSpeck) * plasterSpeckFade;
plasterAlbedo *= mix(1.0, 0.58, plasterPin);
plasterAlbedo *= 0.94 + plasterMicro * 0.12;
plasterAlbedo = mix(plasterAlbedo, plasterAlbedo * vec3(0.86, 0.82, 0.76), clamp(uPlasterWearAmount, 0.0, 1.0) * 0.55);
if (uPlasterInteriorMode > 0.5) {
  // Fresh chalk fracture. Tonal anchor: appearance.fragment.interior #efe8dc.
  // Coarse break with distinct darker cavity patches and visible edges.
  vec3 plasterInteriorTone = vec3(0.937, 0.910, 0.863);
  float chalkTear = smoothstep(0.46, 0.54, gPlasterBroad);
  float chalkChip = smoothstep(0.62, 0.74, gPlasterPores);
  float chalkCavity = smoothstep(0.70, 0.82, gPlasterPores) * smoothstep(0.35, 0.55, 1.0 - gPlasterBroad);
  plasterAlbedo = mix(plasterInteriorTone * 0.40, plasterInteriorTone * 1.20, chalkTear);
  plasterAlbedo = mix(plasterAlbedo, plasterAlbedo * vec3(0.34, 0.30, 0.24), chalkChip * 0.88);
  plasterAlbedo = mix(plasterAlbedo, plasterInteriorTone * vec3(0.20, 0.18, 0.15), chalkCavity * 0.94);
  float chalkEdge = 1.0 - smoothstep(0.0, 0.045, abs(gPlasterBroad - 0.50));
  float chalkEdgeFade = surfaceOctaveFade(max(uPlasterTextureScale, 0.05) * 0.12, surfacePixelFootprint(vPlasterRestPosition));
  plasterAlbedo = mix(plasterAlbedo, plasterAlbedo * vec3(0.36, 0.34, 0.30), chalkEdge * 0.70 * chalkEdgeFade);
}
#ifdef USE_DAMAGE_FIELD
fillDamageGlobals(vPlasterRestPosition, normalize(vPlasterRestNormal));
float plasterFresh = 1.0 - gDamageAge * 0.32;
vec3 plasterDamageInterior = vec3(0.937, 0.910, 0.863);
plasterAlbedo = mix(plasterAlbedo, plasterDamageInterior, gDamageBlotch * mix(0.28, 0.58, clamp(gDamage, 0.0, 1.0)) * plasterFresh);
plasterAlbedo = mix(plasterAlbedo, plasterAlbedo * vec3(0.18, 0.16, 0.14), gDamageCore * 0.78 * plasterFresh);
plasterAlbedo = mix(plasterAlbedo, plasterAlbedo * vec3(0.16, 0.14, 0.12), gDamageCrack * plasterFresh);
#endif
diffuseColor.rgb = plasterAlbedo;`,
    )
    .replace(
      '#include <roughnessmap_fragment>',
      `#include <roughnessmap_fragment>
roughnessFactor = mix(uPlasterRoughnessMin, uPlasterRoughnessMax, gPlasterPores);
roughnessFactor = clamp(roughnessFactor + (plasterMicro - 0.5) * 0.06, uPlasterRoughnessMin, uPlasterRoughnessMax);
roughnessFactor *= mix(1.0, 0.78, clamp(uPlasterClearcoat / 0.4, 0.0, 1.0) * (1.0 - uPlasterClearcoatRoughness));
roughnessFactor = clamp(roughnessFactor + (1.0 - plasterLodFade) * 0.04, uPlasterRoughnessMin, uPlasterRoughnessMax);
if (uPlasterInteriorMode > 0.5) {
  roughnessFactor = mix(min(1.0, uPlasterRoughnessMax + 0.04), uPlasterRoughnessMin + 0.08, gPlasterPores);
  roughnessFactor = clamp(roughnessFactor, uPlasterRoughnessMin, min(1.0, uPlasterRoughnessMax + 0.06));
}
roughnessFactor = clamp(roughnessFactor + gSurfaceSpecAaRough, 0.04, 1.0);
#ifdef USE_DAMAGE_FIELD
roughnessFactor = clamp(roughnessFactor + gDamageBlotch * 0.07 + gDamageCrack * 0.08, 0.04, 1.0);
#endif
`,
    )
    .replace(
      '#include <normal_fragment_maps>',
      `#include <normal_fragment_maps>
vec3 plasterBaseN = normalize(vPlasterRestNormal);
float plasterPoreFade = surfaceOctaveFade(max(uPlasterTextureScale, 0.05) / max(uPlasterPoreScale, 0.2), surfacePixelFootprint(vPlasterRestPosition));
vec3 plasterRelief = surfaceHeightRestNormal(plasterBaseN, vPlasterRestPosition, gPlasterHeight, uPlasterNormalStrength * mix(0.85, 1.15, step(0.5, uPlasterInteriorMode)) * plasterPoreFade);
vec3 plasterMicroN = surfaceMicroNormal(plasterBaseN, vPlasterRestPosition, max(uPlasterTextureScale * 0.09, 0.012), uPlasterNormalStrength * 0.22, uPlasterSeed + 29.0);
normal = normalize(normal + normalMatrix * ((plasterRelief - plasterBaseN) + (plasterMicroN - plasterBaseN)));
roughnessFactor = clamp(roughnessFactor + gSurfaceSpecAaRough, 0.04, 1.0);`,
    )
    .replace(
      '#include <aomap_fragment>',
      `#include <aomap_fragment>
reflectedLight.indirectDiffuse *= 1.0 - (gPlasterPores - 0.5) * 0.06;`,
    );
}

function applyPlasterDefaults(material: THREE.MeshPhysicalMaterial, settings: PlasterMaterialSettings): void {
  applySurfaceMaterialDefaults(material, {
    roughness: settings.roughness,
    metalness: 0,
    shaderOwnsAlbedo: true,
  });
  material.clearcoat = settings.clearcoat;
  material.clearcoatRoughness = settings.clearcoatRoughness;
}

export function createPlasterMaterial(
  params: SurfaceMaterialParams,
  settings: PlasterMaterialSettings,
  face: PlasterFace,
): THREE.MeshPhysicalMaterial {
  const normalized = normalizeSettings(settings);
  const material = new THREE.MeshPhysicalMaterial({
    ...params,
    roughness: normalized.roughness,
    metalness: 0,
    clearcoat: normalized.clearcoat,
    clearcoatRoughness: normalized.clearcoatRoughness,
    flatShading: params.flatShading ?? true,
    side: params.side ?? THREE.FrontSide,
  });
  applyPlasterDefaults(material, normalized);
  const state: PlasterMaterialState = { face, settings: normalized, uniforms: createUniforms(normalized, face) };
  material.onBeforeCompile = (shader) => {
    compilePlasterShader(shader, state);
    applyBakeShaderHooks(shader, material);
  };
  material.customProgramCacheKey = () =>
    surfaceProgramCacheKey('plaster', 4, state.face, state.settings.quality, ...damageFieldProgramParts());
  plasterStates.set(material, state);
  registerDamageFieldConsumer(material);
  return material;
}

export function isPlasterMaterial(material: THREE.Material): material is THREE.MeshPhysicalMaterial {
  return material instanceof THREE.MeshPhysicalMaterial && plasterStates.has(material);
}

export function updatePlasterMaterial(material: THREE.Material, settings: PlasterMaterialSettings): void {
  const state = plasterStates.get(material);
  if (!state) throw new TypeError('updatePlasterMaterial expects a material created by createPlasterMaterial');
  state.settings = normalizeSettings(settings);
  syncUniforms(state);
  if (material instanceof THREE.MeshPhysicalMaterial) {
    applyPlasterDefaults(material, state.settings);
    material.needsUpdate = true;
  }
}

export function clonePlasterMaterial(source: THREE.Material): THREE.MeshPhysicalMaterial {
  const state = plasterStates.get(source);
  if (!state || !(source instanceof THREE.MeshPhysicalMaterial)) {
    throw new TypeError('clonePlasterMaterial expects a material created by createPlasterMaterial');
  }
  const clone = createPlasterMaterial(
    {
      color: source.color.clone(),
      roughness: source.roughness,
      metalness: source.metalness,
      flatShading: source.flatShading,
      side: source.side,
      transparent: source.transparent,
      opacity: source.opacity,
      depthWrite: source.depthWrite,
      alphaTest: source.alphaTest,
      vertexColors: source.vertexColors,
      name: source.name,
    },
    state.settings,
    state.face,
  );
  copySurfaceMaterialProperties(source, clone);
  clone.clearcoat = source.clearcoat;
  clone.clearcoatRoughness = source.clearcoatRoughness;
  return clone;
}

const PLASTER_CONTROLS: SurfaceControlSpec[] = [
  ...commonSurfaceControls(DEFAULT_PLASTER_SETTINGS),
  {
    key: 'broadScale',
    label: 'Broad scale',
    kind: 'range',
    min: 0.12,
    max: 1.2,
    step: 0.01,
    default: DEFAULT_PLASTER_SETTINGS.broadScale,
    group: 'detail',
  },
  {
    key: 'poreScale',
    label: 'Pore scale',
    kind: 'range',
    min: 1.2,
    max: 8,
    step: 0.05,
    default: DEFAULT_PLASTER_SETTINGS.poreScale,
    group: 'detail',
  },
  {
    key: 'clearcoat',
    label: 'Sheen (clearcoat)',
    kind: 'range',
    min: 0,
    max: 0.4,
    step: 0.01,
    default: DEFAULT_PLASTER_SETTINGS.clearcoat,
    group: 'advanced',
  },
  {
    key: 'clearcoatRoughness',
    label: 'Sheen roughness',
    kind: 'range',
    min: 0.2,
    max: 1,
    step: 0.01,
    default: DEFAULT_PLASTER_SETTINGS.clearcoatRoughness,
    group: 'advanced',
  },
];

export const SURFACE_DEFINITION: SurfaceDefinition<PlasterMaterialSettings> = {
  id: 'plaster',
  label: 'Plaster',
  appearance: appearanceDescriptor,
  physics: DESTRUCTION_PROFILES.plaster,
  defaultSettings: DEFAULT_PLASTER_SETTINGS,
  controls: PLASTER_CONTROLS,
  settingsFromControls(values) {
    return normalizeSettings({
      ...DEFAULT_PLASTER_SETTINGS,
      seed: asFiniteNumber(values.seed, DEFAULT_PLASTER_SETTINGS.seed),
      textureScale: asFiniteNumber(values.textureScale, DEFAULT_PLASTER_SETTINGS.textureScale),
      baseTint: asHexColor(values.baseTint, DEFAULT_PLASTER_SETTINGS.baseTint),
      roughnessMin: asFiniteNumber(values.roughnessMin, DEFAULT_PLASTER_SETTINGS.roughnessMin),
      roughnessMax: asFiniteNumber(values.roughnessMax, DEFAULT_PLASTER_SETTINGS.roughnessMax),
      normalStrength: asFiniteNumber(values.normalStrength, DEFAULT_PLASTER_SETTINGS.normalStrength),
      wearAmount: asFiniteNumber(values.wearAmount, DEFAULT_PLASTER_SETTINGS.wearAmount),
      broadScale: asFiniteNumber(values.broadScale, DEFAULT_PLASTER_SETTINGS.broadScale),
      poreScale: asFiniteNumber(values.poreScale, DEFAULT_PLASTER_SETTINGS.poreScale),
      clearcoat: asFiniteNumber(values.clearcoat, DEFAULT_PLASTER_SETTINGS.clearcoat),
      clearcoatRoughness: asFiniteNumber(values.clearcoatRoughness, DEFAULT_PLASTER_SETTINGS.clearcoatRoughness),
    });
  },
  createMaterial(params, settings, face) {
    return createPlasterMaterial(params, settings, face);
  },
  updateMaterial: updatePlasterMaterial,
  cloneMaterial: clonePlasterMaterial,
  isMaterial: isPlasterMaterial,
};
